import { GuideTopic } from "../GuideBook";
import { LocatorScene } from "../art/locator";
import SceneLoop from "../art/SceneLoop";
import {
  ClustersScene,
  FilterScene,
  SearchScene,
  PanelScene,
  SelectionScene,
  SnapshotScene,
  StatusScene,
  RolesScene,
  TrainScene,
  FolderScene,
} from "../art/scenes";
import { PropagateScene } from "views/components/LabelPropagation/guides/art/SeedScenes";

export const EXPLORATION_TOPICS: GuideTopic[] = [
  {
    key: "explore",
    label: "Explore view",
    title: "Clusters of similar images",
    body: (
      <>
        <p>
          The <strong>Explore</strong> tab groups images into clusters of
          visually similar content. Each card shows the number of images, its
          most frequent labels and a few representative thumbnails.
        </p>
        <ul>
          <li>Click a cluster to see all of its images.</li>
          <li>Click an image to open it with zoom and its metadata.</li>
          <li>
            The <strong>granularity</strong> slider in the footer makes clusters
            larger or finer.
          </li>
          <li>
            The <strong>Data</strong> tab next to Explore adds media to the
            dataset.
          </li>
        </ul>
      </>
    ),
    tip: "The pink box on every image marks the inspected area. Only a defect inside the box counts for that image, when seeding and when reviewing.",
    art: (
      <SceneLoop
        scenes={[
          <LocatorScene key="where" target="grid" label="Cluster grid" />,
          <ClustersScene key="how" />,
        ]}
      />
    ),
  },
  {
    key: "granularity",
    label: "Granularity",
    title: "Larger or finer clusters",
    body: (
      <p>
        The slider in the bottom right controls how similar images must be to
        share a cluster. Move it left for fewer, larger clusters and right for
        more, smaller ones. It is locked while the Duplicates filter is on.
      </p>
    ),
    art: (
      <SceneLoop
        scenes={[
          <LocatorScene
            key="where"
            target="granularity"
            label="Granularity slider"
          />,
          <ClustersScene key="how" />,
        ]}
      />
    ),
  },
  {
    key: "filters",
    label: "Filters",
    title: "Narrow the view",
    body: (
      <>
        <p>
          Click <strong>Add Filter</strong> in the toolbar and choose Folders,
          Files, Labels, User Tags or one of the quality filters: Mislabels,
          Class Outliers, Duplicates, Outliers, Quality Issues, Select Uniques,
          Insertion Time and Media Status.
        </p>
        <ul>
          <li>Different filters combine with AND.</li>
          <li>Selections inside one filter combine with OR.</li>
          <li>Text filters are case sensitive.</li>
          <li>Quality filters only appear when the data has matches.</li>
        </ul>
      </>
    ),
    tip: "Active filters show as chips above the grid. Click a chip to edit it, or Reset to clear all.",
    art: (
      <SceneLoop
        scenes={[
          <LocatorScene key="where" target="filters" label="Add Filter" />,
          <FilterScene key="how" />,
        ]}
      />
    ),
  },
  {
    key: "search",
    label: "Search",
    title: "Search and Find Similar",
    body: (
      <>
        <p>
          Type in the search bar to find images by label or caption. Text search
          becomes available once the dataset has labels.
        </p>
        <ul>
          <li>
            Click the image icon in the bar to upload a picture and search by
            appearance.
          </li>
          <li>
            Hover any image or cluster and click <strong>Find Similar</strong>.
          </li>
          <li>
            Use <strong>Minimum Relevance</strong> to tighten or loosen the
            results.
          </li>
        </ul>
      </>
    ),
    tip: "Crop an uploaded image to the defect. Otherwise the whole picture, including background, is matched.",
    art: (
      <SceneLoop
        scenes={[
          <LocatorScene key="where" target="search" label="Search bar" />,
          <SearchScene key="how" />,
        ]}
      />
    ),
  },
  {
    key: "insights",
    label: "Insights Panel",
    title: "Live summary of the current view",
    body: (
      <>
        <p>
          The panel on the right updates as you filter. Sections: Enrichment
          models, Issues, Labels, User Tags and Metadata. Switch between counts
          and percentages with the <strong>#</strong> and <strong>%</strong>{" "}
          toggles.
        </p>
        <p>
          Two independent checkmarks control which labels are shown:{" "}
          <strong>VL Label Propagation</strong> (propagated labels) and{" "}
          <strong>User Labels</strong> (labels imported from the AOI output).
        </p>
      </>
    ),
    tip: "Deselect one source to compare propagated labels with the original AOI classifications.",
    art: (
      <SceneLoop
        scenes={[
          <LocatorScene key="where" target="insights" label="Insights Panel" />,
          <PanelScene key="how" />,
        ]}
      />
    ),
  },
  {
    key: "lp",
    label: "Label Propagation",
    title: "Label the dataset from a few seeds",
    body: (
      <>
        <p>
          The second tab at the top of the right panel opens{" "}
          <strong>Label Propagation</strong>. Give each label 5 seed images,
          review the batches it is unsure about, and it labels the rest. The tab
          has its own step-by-step guide.
        </p>
        <ul>
          <li>
            <strong>
              Running it again replaces all previous propagated labels.
            </strong>{" "}
            To keep them, filter by each label and drag those images onto its
            seed card before starting the new run.
          </li>
          <li>
            Seeds lock once you start. <strong>Reset</strong> is the only way to
            change them, and it deletes the run's progress.
          </li>
          <li>
            <strong>Evaluate Label Propagation</strong> in the header's More
            Actions menu appears after a run. Upload a ground-truth DB.csv and
            classes.json to download an HTML accuracy report.
          </li>
        </ul>
      </>
    ),
    tip: "The dataset is Read Only while a cycle runs. Snapshots and media addition wait until it finishes.",
    art: (
      <SceneLoop
        scenes={[
          <LocatorScene
            key="where"
            target="lpTab"
            label="Label Propagation tab"
          />,
          <PropagateScene key="how" />,
        ]}
      />
    ),
  },
  {
    key: "addMedia",
    label: "Add media",
    title: "Add new production data to this dataset",
    body: (
      <>
        <p>
          Open the <strong>Data</strong> tab (next to Explore) to add folders to
          an existing dataset instead of creating a new one. The same wizard
          validates each folder; only folders with the same Job, Setup, Recipe
          and class list can be added.
        </p>
        <ul>
          <li>
            New images appear right away in a <strong>New Media</strong> cluster
            and the dataset shows <strong>Partial Index</strong>.
          </li>
          <li>
            Add as many batches as you need, then click the status badge and{" "}
            <strong>Re-Index</strong> once. Re-indexing re-clusters everything
            and makes the new images available to Label Propagation and
            training.
          </li>
          <li>
            After adding media the Labels section may list everything as
            unlabeled. This is a display limitation; earlier propagated labels
            are kept and become seed candidates.
          </li>
          <li>
            To label the new images, run Label Propagation again and re-add the
            existing labeled images as seeds so they are preserved.
          </li>
        </ul>
      </>
    ),
    tip: "While media is being added the dataset is Read Only and Label Propagation cannot start.",
    art: (
      <SceneLoop
        scenes={[
          <LocatorScene key="where" target="tabs" label="Data tab" />,
          <FolderScene key="how" />,
        ]}
      />
    ),
  },
  {
    key: "selection",
    label: "Select and export",
    title: "Select, tag and export",
    body: (
      <>
        <ul>
          <li>
            Click to select, Shift click for a range, Ctrl or Cmd A for all.
          </li>
          <li>
            The floating bar lets you <strong>Assign Tag</strong> or{" "}
            <strong>Keep Selection</strong> in the cart (header icon).
          </li>
          <li>
            The <strong>Export</strong> icon in the header writes a JSON file
            for the selection, the filtered view, the whole dataset or the Label
            Propagation results.
          </li>
        </ul>
      </>
    ),
    tip: "Export includes up to 5,000 images. User tags are for organizing only and are not sent to training.",
    art: (
      <SceneLoop
        scenes={[
          <LocatorScene key="where" target="export" label="Export" />,
          <SelectionScene key="how" />,
        ]}
      />
    ),
  },
  {
    key: "snapshots",
    label: "Snapshots",
    title: "Saved states of the dataset",
    body: (
      <>
        <p>
          The snapshot icon in the header opens the Snapshot Catalog. Visual
          Layer creates a snapshot before every pipeline run, and you can create
          one manually while the dataset is Ready.
        </p>
        <ul>
          <li>
            <strong>Restore</strong> rolls the dataset back to that state.
          </li>
          <li>
            <strong>Clone</strong> creates a new dataset from it.
          </li>
          <li>
            <strong>Recover</strong> in the inventory uses the last snapshot
            after an error.
          </li>
        </ul>
      </>
    ),
    tip: "Snapshot actions need the Editor or Owner role and are blocked while Label Propagation runs.",
    art: (
      <SceneLoop
        scenes={[
          <LocatorScene key="where" target="snapshots" label="Snapshots" />,
          <SnapshotScene key="how" />,
        ]}
      />
    ),
  },
  {
    key: "share",
    label: "Share",
    title: "Give teammates access",
    body: (
      <>
        <p>
          The share icon in the header opens the <strong>Share Dataset</strong>{" "}
          panel. The creator and the workspace Admin are Owners; other workspace
          members start as Viewers.
        </p>
        <ul>
          <li>
            Invite a user and pick <strong>Editor</strong> or{" "}
            <strong>Viewer</strong>.
          </li>
          <li>
            Change or remove access from the <strong>Who Has Access</strong>{" "}
            list. Changes apply immediately.
          </li>
          <li>Workspace Viewers can only be given Viewer access.</li>
        </ul>
      </>
    ),
    tip: "Editors can add media, run Label Propagation, use snapshots and send to training. Viewers explore and export.",
    art: (
      <SceneLoop
        scenes={[
          <LocatorScene key="where" target="share" label="Share" />,
          <RolesScene key="how" />,
        ]}
      />
    ),
  },
  {
    key: "status",
    label: "Status badge",
    title: "What the status badge means",
    body: (
      <>
        <ul>
          <li>
            <strong>Ready</strong>: everything is available.
          </li>
          <li>
            <strong>Read Only</strong>: a task is running (Label Propagation,
            media addition, snapshot). You can explore, filter, share and tag,
            but not change data.
          </li>
          <li>
            <strong>Partial Index</strong>: new media was added but not
            re-indexed. Click the badge and choose <strong>Re-Index</strong>{" "}
            once you have added everything you plan to.
          </li>
          <li>
            <strong>Indexing</strong>: creation or Re-Index is running.
          </li>
          <li>
            <strong>Error</strong>: the last task failed. Recover or delete from
            the inventory card menu.
          </li>
        </ul>
      </>
    ),
    tip: "Hover the badge for details and a link to the Task Manager.",
    art: (
      <SceneLoop
        scenes={[
          <LocatorScene key="where" target="status" label="Status badge" />,
          <StatusScene key="how" />,
        ]}
      />
    ),
  },
  {
    key: "modelJobs",
    label: "Model Jobs",
    title: "Train or validate a model",
    body: (
      <>
        <p>
          <strong>Model Jobs</strong> in the header opens a dialog with two
          choices.
        </p>
        <ul>
          <li>
            <strong>Train</strong>: packages the propagated labels and sends
            them to the Camtek training infrastructure. It is disabled until
            Label Propagation has run and the dataset is Train-Worthy. Pick a
            model configuration from the list and click Train.
          </li>
          <li>
            <strong>Validate</strong>: runs a trained model on this dataset.
            Choose a label source (User Labels, Label Propagation or Unlabeled),
            then a model. Keep <strong>Show only compatible models</strong> on
            to see models that match this Job, Setup and Recipe.
          </li>
        </ul>
        <p>
          Progress appears next to the button and in the Task Manager. When a
          job completes, a badge offers <strong>View Model</strong> in the Model
          Catalog.
        </p>
      </>
    ),
    tip: "One training or validation job per dataset at a time. Validation keeps the dataset Ready; Editors and Owners can stop a job.",
    art: (
      <SceneLoop
        scenes={[
          <LocatorScene key="where" target="modelJobs" label="Model Jobs" />,
          <TrainScene key="how" />,
        ]}
      />
    ),
  },
];
